import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import swal from 'sweetalert';
import { getDestinationAdmin, createDestination, updateDestination } from '../../../api/contentAdminApi';
import { getDistricts } from '../../../api/destinationApi';
import { DESTINATION_CATEGORIES } from '../../../data/cmsAdminConfig';
import { CmsEditorialFields, CmsFormShell } from './CmsFormFields';
import { normalizeCmsFormData, prepareCmsPayload } from '../../../utils/cmsPublishHelpers';

const EMPTY = {
	name: '', slug: '', category: 'viewpoint', summary: '', body: '', district_id: '',
	hero_image_url: '', best_season: '', difficulty: '', editorial_status: 'draft',
};

export default function DestinationAdminForm() {
	const { slug } = useParams();
	const history = useHistory();
	const isEdit = Boolean(slug);
	const [form, setForm] = useState(EMPTY);
	const [districts, setDistricts] = useState([]);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		getDistricts({ per_page: 200 }).then((r) => setDistricts(r.data?.districts || r.data || [])).catch(() => {});
	}, []);

	useEffect(() => {
		if (!isEdit) return;
		getDestinationAdmin(slug)
			.then((res) => setForm(normalizeCmsFormData(res.data?.destination || res.data, EMPTY)))
			.catch(() => swal('Error', 'Could not load destination', 'error'));
	}, [isEdit, slug]);

	const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSaving(true);
		try {
			const payload = prepareCmsPayload(form);
			if (isEdit) await updateDestination(slug, payload);
			else await createDestination(payload);
			swal('Saved', 'Destination saved.', 'success');
			history.push('/admin/cms/destinations');
		} catch (err) {
			swal('Error', err.response?.data?.errors?.[0] || 'Could not save', 'error');
		} finally {
			setSaving(false);
		}
	};

	return (
		<CmsFormShell title={isEdit ? `Edit: ${form.name}` : 'New destination'} backHref="/admin/cms/destinations" onSubmit={handleSubmit} saving={saving}>
			<fieldset className="admin-cms-form__section">
				<legend>Basics</legend>
				<div className="admin-cms-form__row">
					<label><span>Name</span><input required value={form.name} onChange={(e) => onChange('name', e.target.value)} /></label>
					<label><span>Slug</span><input required value={form.slug} onChange={(e) => onChange('slug', e.target.value)} disabled={isEdit} /></label>
				</div>
				<div className="admin-cms-form__row">
					<label>
						<span>Category</span>
						<select value={form.category} onChange={(e) => onChange('category', e.target.value)}>
							{DESTINATION_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
						</select>
					</label>
					<label>
						<span>District</span>
						<select required value={form.district_id || ''} onChange={(e) => onChange('district_id', e.target.value)}>
							<option value="">Select district…</option>
							{districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
						</select>
					</label>
				</div>
				<label><span>Summary</span><textarea rows={2} value={form.summary || ''} onChange={(e) => onChange('summary', e.target.value)} /></label>
				<label><span>Body</span><textarea rows={8} value={form.body || ''} onChange={(e) => onChange('body', e.target.value)} /></label>
				<label><span>Hero image URL</span><input value={form.hero_image_url || ''} onChange={(e) => onChange('hero_image_url', e.target.value)} /></label>
				<div className="admin-cms-form__row">
					<label><span>Best season</span><input value={form.best_season || ''} onChange={(e) => onChange('best_season', e.target.value)} /></label>
					<label><span>Difficulty</span><input value={form.difficulty || ''} onChange={(e) => onChange('difficulty', e.target.value)} /></label>
				</div>
			</fieldset>
			<CmsEditorialFields form={form} onChange={onChange} />
		</CmsFormShell>
	);
}
